'use client'

import { memo } from "react"
import { usePathname } from "next/navigation"
import { formatImageURL, formatPrice, getAspectRatioClass, getBorderRadiusClass, getVendorPrimaryColor, getBackgroundColorStyles, getEffectivePrice, getStrikethroughPrice, getDisplayDiscounts, isDiscountBetter, isCustomerAuthActive, getBuyXGetYTagText, getSalePercent, getOtherVariantSaleType, getLoyaltyProductDiscount } from "@/utils/util"
import { BadgeDollarSign, BadgePercent, Camera, Gift, Heart, LoaderCircle, Medal, PackageX, Percent, ShoppingBasket, ShoppingCart, Star } from "lucide-react"
import { Button } from "@/components/elements/Button"
import { navigateUrl } from "@/utils/client-request"
import { useCart, useWishlist, useProductCard, useImageCarousel } from "@/lib/commerce/hooks"
import { useSession } from "next-auth/react"
import VariantSelectionModal from "@/components/blocks/VariantSelectionModal"
import NoImage from "@/components/blocks/NoImage"
import ColorSwatches from "@/components/blocks/ColorSwatches"

function ProductCard({ product, vendor, showVariantName = false }: { product: any, vendor: any, showVariantName?: boolean }) {
  const variantName = product?.variant?.name
  const { data: session } = useSession()
  const pathname = usePathname()
  const { isInWishlist, toggle: toggleWishlistHook, loading: wishlistLoading } = useWishlist()
  const { isProductAvailable, availableVariant, loading, showVariantModal, setShowVariantModal, handleAddToCart, handleVariantAddToCart, colors, activeColor, selectColor, cardProduct } = useProductCard(product, vendor, showVariantName)
  const localIsInWishlist = isInWishlist(product.id)
  const { hoveredImageIndex, setHoveredImageIndex, allImages, remainingCount, shouldLoadImage, hasImages, handleTouchStart, handleTouchEnd, onMouseEnter, onMouseLeave } = useImageCarousel(cardProduct)

  const borderRadius = vendor?.border ? getBorderRadiusClass(vendor.border) : 'rounded-md'
  const aspectRatioClass = getAspectRatioClass(vendor?.imageAspectRatio)

  const primaryColor = getVendorPrimaryColor(vendor)

  const { className: backgroundColorClass, style: backgroundColorStyle } = getBackgroundColorStyles(primaryColor)

  const toggleWishlist = () => toggleWishlistHook({ productId: product.id, variantId: product.variant?.id })

  const discounts = getDisplayDiscounts(product, !!session?.user?.id, availableVariant?.id)
  const effectivePrice = getEffectivePrice(availableVariant, discounts)
  const strikethroughPrice = getStrikethroughPrice(availableVariant, discounts)
  const variantHasPriceSale = !!(availableVariant?.priceSale && availableVariant?.price && availableVariant.priceSale < availableVariant.price)
  const showsDiscountBadge = discounts?.length > 0 && isDiscountBetter(availableVariant, discounts)
  const { loyaltySummary, loyaltyCategoryParents } = useCart()
  const loyaltyDeal = getLoyaltyProductDiscount({ product, variant: availableVariant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: showsDiscountBadge })
  const displayEffective = loyaltyDeal ? Math.max(0, (effectivePrice ?? availableVariant?.price ?? 0) - loyaltyDeal.unitSaving) : effectivePrice
  const displayStrike = loyaltyDeal ? (availableVariant?.price ?? 0) : strikethroughPrice
  const otherVariantSale = (!variantHasPriceSale && !(showsDiscountBadge && isProductAvailable))
    ? getOtherVariantSaleType(product, availableVariant?.id, !!session?.user?.id)
    : null

  return (
    <div className="h-full flex flex-col justify-between group text-sm cursor-pointer">
      <div>
        <div className={`${aspectRatioClass} w-full relative overflow-hidden ${borderRadius} bg-zinc-100 border border-slate-100`} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          {!hasImages ? <NoImage /> : allImages.map((img: string, index: number) => shouldLoadImage(index) && (
            <img key={index} src={formatImageURL(img)} alt={product?.name} loading="lazy" decoding="async" className={`w-full h-full object-cover object-center absolute inset-0 transition-opacity duration-300 ${hoveredImageIndex === index ? 'opacity-100' : 'opacity-0'}`} />
          ))}
          {remainingCount > 0 && hoveredImageIndex === 4 && (
            <div className="absolute inset-0 bg-black/50 flex flex-col gap-1 items-center justify-center">
              <Camera className="h-8 w-8 text-white" />
              <span className="text-white font-semibold text-lg">+{remainingCount} зураг</span>
            </div>
          )}
          <div className="w-full">
            <a className="w-full h-full block" href={navigateUrl(`/product/${product.customId}`, pathname)}></a>
            {!isProductAvailable ? (
              <div className={`absolute top-2 left-2 bg-linear-to-r from-foreground/50 to-foreground/60 text-white px-1.75 py-1 rounded-full font-bold text-xs leading-4 z-10`}>
                <span>Дууссан</span>
              </div>
            ) : discounts?.length > 0 && isDiscountBetter(availableVariant, discounts) ? (
              <div className="absolute top-2 left-2 z-10 flex flex-wrap gap-1">
                <div className="flex gap-1">
                  {discounts.map((discount: any, index: number) => (
                    <div key={discount.id || index}>
                      {discount.type === 'productDiscount' && discount.valueType === 'percent' && discount.valuePercent ? (
                        discount.valuePercent >= 100 ? (
                          <div className="bg-green-200 text-green-700 px-1.5 py-1 rounded-full font-bold tabular-nums leading-4 text-xs flex items-center gap-0.5">
                            <Gift className="h-4 w-4" />
                            <span>Үнэгүй</span>
                          </div>
                        ) : (
                          <div className="bg-orange-600 text-white px-1.5 py-1 rounded-full font-bold tabular-nums leading-4 text-xs flex items-center gap-0.5">
                            <BadgePercent className="h-4 w-4 fill-orange-600 bg-orange-600" />
                            <span>-{discount.valuePercent}%</span>
                          </div>
                        )
                      ) : discount.type === 'productDiscount' && discount.valueType === 'amount' && discount.valueAmount ? (
                        <div className="bg-blue-400 text-white px-1.5 py-1 rounded-full font-bold tabular-nums leading-4 text-xs flex items-center gap-0.5">
                          <BadgeDollarSign className="h-4 w-4 fill-blue-400 bg-blue-400" />
                          <span>-{formatPrice(discount.valueAmount)}</span>
                        </div>
                      ) : discount.type === 'buyXgetY' ? (
                        <div className="bg-green-200 text-green-700 px-1.5 py-1 rounded-full font-bold tabular-nums leading-4 text-xs flex items-center gap-0.5">
                          <Gift className="h-4 w-4" />
                          <span>{getBuyXGetYTagText(discount)}</span>
                        </div>
                      ) : null}
                    </div>
                  ))}
                  {!discounts.some((d: any) => d.type === 'productDiscount') && variantHasPriceSale && !loyaltyDeal?.saleOffset && (
                    <div className="bg-orange-600 text-white px-1.5 py-1 rounded-full font-bold tabular-nums leading-4 text-xs flex items-center gap-0.5">
                      <BadgePercent className="h-4 w-4 fill-orange-600 bg-orange-600" />
                      <span>-{getSalePercent(availableVariant)}%</span>
                    </div>
                  )}
                  {loyaltyDeal && (
                    <div className="bg-violet-600 text-white px-1.5 py-1 rounded-full font-bold tabular-nums leading-4 text-xs flex items-center gap-0.5">
                      <Medal className="h-4 w-4" />
                      <span>-{loyaltyDeal.percent}%</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              (variantHasPriceSale || loyaltyDeal) ? (
                <div className="absolute top-2 left-2 z-10 flex flex-wrap gap-1">
                  {variantHasPriceSale && !loyaltyDeal?.saleOffset && (
                    <div className={`bg-orange-600 text-white px-1.5 py-1 rounded-full font-bold tabular-nums leading-4 text-xs flex items-center gap-0.5`}>
                      <BadgePercent className={`h-4 w-4 fill-orange-600 bg-orange-600`} />
                      <span>-{getSalePercent(availableVariant)}%</span>
                    </div>
                  )}
                  {loyaltyDeal && (
                    <div className="bg-violet-600 text-white px-1.5 py-1 rounded-full font-bold tabular-nums leading-4 text-xs flex items-center gap-0.5">
                      <Medal className="h-4 w-4" />
                      <span>-{loyaltyDeal.percent}%</span>
                    </div>
                  )}
                </div>
              ) : null
            )}
            {isCustomerAuthActive(vendor) && (
              <div onClick={() => toggleWishlist()} className={`absolute top-2 right-2 bg-linear-to-b from-foreground/24 to-foreground/16 backdrop-blur-[20px] shadow-[0_4px_4px_rgba(0,0,0,0.05)] transition-all p-2 rounded-full hidden group-hover:inline-block w-fit h-fit z-10`}>
                {wishlistLoading ? (
                  <LoaderCircle className={`h-5 w-5 text-white animate-spin`} />
                ) : (
                  <Heart className={`h-5 w-5 ${localIsInWishlist ? `text-white fill-white` : `text-white`}`} />
                )}
              </div>
            )}
            {otherVariantSale && !loyaltyDeal && (
              <div className="absolute top-2 left-2 z-10 px-1.5 py-1 bg-orange-600 text-white rounded-full">
                {otherVariantSale === 'discount' ? <Gift className="w-4 h-4" strokeWidth={2.5} /> : <Percent className="w-4 h-4" strokeWidth={2.5} />}
              </div>
            )}
            {allImages.length > 1 && (
              <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
                <div className="bg-linear-to-t from-black/30 via-black/15 to-transparent pt-4 pb-16 px-2 flex gap-1 items-start">
                  {allImages.map((_: string, index: number) => (
                    <div key={index} onMouseEnter={() => setHoveredImageIndex(index)} className="flex-1 h-5 cursor-pointer flex items-end pointer-events-auto">
                      <div className={`w-full h-0.75 rounded-full transition-all ${hoveredImageIndex === index ? 'bg-white' : 'bg-white/50'} drop-shadow-sm`} />
                    </div>
                  ))}
                </div>
              </div>
            )}
            {allImages.length > 1 && (
              <div className="absolute bottom-0 left-0 right-0 z-20 sm:hidden pointer-events-none">
                <div className="bg-linear-to-t from-black/30 to-transparent pt-4 pb-2 flex justify-center gap-1.5">
                  {allImages.map((_: string, index: number) => (
                    <div key={index} className={`w-2 h-2 rounded-full transition-all ${hoveredImageIndex === index ? 'bg-white' : 'bg-white/50'} drop-shadow-sm`} />
                  ))}
                </div>
              </div>
            )}
            {vendor?.canPlaceOrder !== false && (
              <div className="absolute w-full bottom-0 px-2 pb-2 pt-6 hidden sm:group-hover:inline-block h-fit z-30 pointer-events-none">
                {isProductAvailable ? (
                  <Button className="w-full text-xs font-semibold rounded-md pointer-events-auto" color={vendor?.color?.primary} icon={ShoppingCart} onClick={handleAddToCart} disabled={loading}>
                    Сагсанд хийх
                  </Button>
                ) : (
                  <Button className="w-full text-xs font-semibold rounded-md pointer-events-auto" icon={PackageX} disabled color={vendor?.color?.primary}>
                    Дууссан
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        <ColorSwatches colors={colors} active={activeColor} onSelect={selectColor} className="hidden sm:flex mt-2 -ml-0.75" />

        <div className="flex items-center gap-2 mt-2 sm:hidden">
          <ColorSwatches colors={colors} active={activeColor} onSelect={selectColor} max={colors.length > 3 ? 2 : 3} className="flex-nowrap shrink-0" />
          {isCustomerAuthActive(vendor) && (
            <div onClick={() => toggleWishlist()} className={`border border-border rounded-md p-1.75 ${vendor?.canPlaceOrder === false ? 'flex-1 flex items-center justify-center' : 'shrink-0'}`}>
              {wishlistLoading ? (
                <LoaderCircle className="h-4 w-4 text-foreground animate-spin" />
              ) : (
                <Heart className={`h-4 w-4 ${localIsInWishlist ? 'text-foreground fill-foreground' : 'text-foreground'}`} />
              )}
            </div>
          )}
          {vendor?.canPlaceOrder !== false && (
            isProductAvailable ? (
              <div onClick={handleAddToCart} className={`${backgroundColorClass} text-white transition-all ${colors.length > 0 ? 'px-0' : 'px-1.5'} py-2.5 rounded-md flex-1 flex items-center justify-center gap-2 cursor-pointer`} style={backgroundColorStyle}>
                <ShoppingBasket className="h-4 w-4 shrink-0 text-white" />
                {colors.length === 0 && <span className="font-normal text-xs leading-4 tracking-[.0125em]">Сагслах</span>}
              </div>
            ) : (
              <div className={`${backgroundColorClass} text-white transition-all ${colors.length > 0 ? 'px-0' : 'px-1.75'} py-2.5 rounded-md flex-1 flex items-center justify-center gap-2 pointer-events-none opacity-50`} style={backgroundColorStyle}>
                <PackageX className="h-4 w-4 shrink-0 text-white" />
                {colors.length === 0 && <span className="font-normal text-xs leading-4 tracking-[.0125em]">Дууссан</span>}
              </div>
            )
          )}
        </div>
      </div>

      <div className="sm:hidden">
        <div className="flex flex-col justify-between h-full mt-3">
          <a href={navigateUrl(`/product/${product.customId}`, pathname)}>
            <div className="font-semibold text-foreground leading-5 line-clamp-3 tracking-[.025em] text-sm">{product?.name}</div>
            {showVariantName && variantName && <div className="text-sm text-muted-foreground">{variantName}</div>}
          </a>
          <div className="flex flex-col gap-1">
            <div>
              {isProductAvailable ? (
                availableVariant?.price && availableVariant?.price > 0 && (
                  displayEffective !== null ? (
                    displayEffective === 0 ? (
                      <div className="font-medium flex items-center gap-1.5">
                        <div className="text-base text-green-600 font-medium leading-5 tracking-[.025em]">Үнэгүй</div>
                        <div className="line-through text-muted-foreground text-sm leading-4 tracking-[.0125em]">{formatPrice(displayStrike)}</div>
                      </div>
                    ) : (
                      <div className="font-medium flex items-center gap-1.5">
                        <div className="text-base text-red-600 font-medium leading-5 tracking-[.025em]">{formatPrice(displayEffective)}</div>
                        <div className="line-through text-muted-foreground text-sm leading-4 tracking-[.0125em]">{formatPrice(displayStrike)}</div>
                      </div>
                    )
                  ) : (
                    <span className="font-medium text-foreground leading-6 text-base tracking-[.025em]">{formatPrice(availableVariant?.price)}</span>
                  )
                )
              ) : (
                <span className="font-medium text-red-600 leading-6 tracking-[.0125em]">Дууссан</span>
              )}
            </div>
            {vendor?.canReview && (
              <div className="flex items-center gap-0.5">
                <div className="flex items-center gap-1">
                  <Star className="text-yellow-500 fill-yellow-500 shrink-0 size-3" />
                  <p className="text-xs leading-4 tracking-[.0125em]">{product.averageRating || 0}</p>
                </div>
                {product.reviewCount > 0 && (
                  <div className='text-muted-foreground text-xs leading-4'>
                    ({product.reviewCount})
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="hidden sm:block h-full">
        <a href={navigateUrl(`/product/${product.customId}`, pathname)}>
          <div className="flex flex-col justify-between h-full mt-3">
            <div>
              <div className="font-semibold text-foreground leading-6 text-base line-clamp-3">{product?.name}</div>
              {showVariantName && variantName && <div className="text-sm text-muted-foreground">{variantName}</div>}
            </div>
            <div className="flex flex-col gap-1.5">
              {isProductAvailable ? (
                availableVariant?.price && availableVariant?.price > 0 && (
                  displayEffective !== null ? (
                    displayEffective === 0 ? (
                      <div className="font-medium text-foreground leading-6 text-base flex items-center gap-1.5">
                        <span className="text-base text-green-600 font-medium leading-5 tracking-[.025em]">Үнэгүй</span>
                        <span className="line-through text-muted-foreground text-sm leading-4 tracking-[.0125em]">{formatPrice(displayStrike)}</span>
                      </div>
                    ) : (
                      <div className="font-medium text-foreground leading-6 text-base flex items-center gap-1.5">
                        <span className="text-base text-red-600 font-medium leading-5 tracking-[.025em]">{formatPrice(displayEffective)}</span>
                        <span className="line-through text-muted-foreground text-sm leading-4 tracking-[.0125em]">{formatPrice(displayStrike)}</span>
                      </div>
                    )
                  ) : (
                    <span className="font-medium text-foreground leading-6 text-base tracking-[.025em]">{formatPrice(availableVariant?.price)}</span>
                  )
                )
              ) : (
                <span className="font-medium text-muted-foreground leading-6 tracking-[.025em] text-sm">Дууссан</span>
              )}
              {vendor?.canReview && (
                <div className="flex items-center gap-0.5">
                  <div className="flex items-center gap-1">
                    <Star aria-hidden="true" className="text-yellow-500 fill-yellow-500 shrink-0 size-3" />
                    <p className="text-xs leading-5 tracking-[.0125em]">{product.averageRating || 0}</p>
                  </div>
                  {product.reviewCount > 0 && (
                    <div className='text-muted-foreground text-xs leading-5'>
                      ({product.reviewCount})
                    </div>
                  )}
                </div>
              )}
              <VariantSelectionModal isOpen={showVariantModal} onClose={() => setShowVariantModal(false)} product={cardProduct} vendor={vendor} onAddToCart={handleVariantAddToCart} />
            </div>
          </div>
        </a>
      </div>
    </div>
  )
}

// memo: appending a page of 20 otherwise re-renders every card already in the grid
export default memo(ProductCard)